//@ts-check

import html2pdf from 'html2pdf.js';

class FinancialSummaryService {
	constructor($http, Utility, $timeout, $rootScope, InternalLinksService, LocalStorageService) {
		this.$http = $http;
		this.Utility = Utility;
		this.$timeout = $timeout;
		this.$rootScope = $rootScope;
		this.LocalStorageService = LocalStorageService;
		this.InternalLinksService = InternalLinksService;

		this.init();
	}

	init() {
		this.financialSummary = {};
		this.buildingWorkflowAndFeatures = {};
	}

	async approve(financialSummaryApproval) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('Approve');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/financial/links');
				url = await this.InternalLinksService.getLink('Approve');
			}

			const building = this._getSelectedBuildingRef();
			url = url.replace(`{schemeRef}`, building);

			const headers = this.Utility.headersWithContentType;
			const results = await this.$http.post(url, financialSummaryApproval, { headers });
			const { WasSuccessful } = results.data;

			if (WasSuccessful) {
				this.$timeout(() => promiseHandler.resolve());
			} else {
				throw new Error(
					`Could not approve the financial summary for Building: ${building}, financialSummaryApproval: ${JSON.stringify(
						financialSummaryApproval
					)}`
				);
			}
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async bookkeeperApprove(financialSummaryApproval) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('BookkeeperApprove');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/financial/links');
				url = await this.InternalLinksService.getLink('BookkeeperApprove');
			}

			const building = this._getSelectedBuildingRef();
			url = url.replace(`{schemeRef}`, building);

			const headers = this.Utility.headersWithContentType;
			const results = await this.$http.post(url, financialSummaryApproval, { headers });
			const { WasSuccessful } = results.data;

			if (WasSuccessful) {
				this.$timeout(() => promiseHandler.resolve());
			} else {
				throw new Error(
					`Could not approve the financial summary for Building: ${building}, financialSummaryApproval: ${JSON.stringify(
						financialSummaryApproval
					)}`
				);
			}
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async reject(RejectionReason, RejectionDate) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('Reject');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/financial/links');
				url = await this.InternalLinksService.getLink('Reject');
			}

			const building = this._getSelectedBuildingRef();
			url = url.replace(`{schemeRef}`, building);

			const { IsNewProcess } = this.financialSummary;

			const summaryRejection = {
				IsNewProcess,
				RejectionDate,
				RejectionReason
			};

			const headers = this.Utility.headersWithContentType;
			const results = await this.$http.post(url, summaryRejection, { headers });
			const { WasSuccessful } = results.data;

			if (WasSuccessful) {
				this.$timeout(() => promiseHandler.resolve());
			} else {
				throw new Error(
					`Could not reject the financial summary for Building: ${building}, Date: ${RejectionDate}`
				);
			}
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async bookkeeperReject(RejectionReason, RejectionDate) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('BookkeeperReject');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/financial/links');
				url = await this.InternalLinksService.getLink('BookkeeperReject');
			}

			const building = this._getSelectedBuildingRef();
			url = url.replace(`{schemeRef}`, building);

			const { IsNewProcess } = this.financialSummary;

			const summaryRejection = {
				IsNewProcess,
				RejectionDate,
				RejectionReason
			};

			const headers = this.Utility.headersWithContentType;
			const results = await this.$http.post(url, summaryRejection, { headers });
			const { WasSuccessful } = results.data;

			if (WasSuccessful) {
				this.$timeout(() => promiseHandler.resolve());
			} else {
				throw new Error(
					`Could not reject the financial summary for Building: ${building}, Date: ${RejectionDate}`
				);
			}
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async getFinancialSummary(schemeRef, date) {
		let promiseHandler = {};
		const promise = new Promise((resolve, reject) => {
			promiseHandler = {
				reject,
				resolve
			};
		});

		try {
			let url = await this.InternalLinksService.getLink('FinancialApiFinancialSummary');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/financial/links');
				url = await this.InternalLinksService.getLink('FinancialApiFinancialSummary');
			}
			url = url.replace('{schemeRef}', schemeRef).trim();

			const headers = this.Utility.headersWithContentType;

			const results = await this.$http.post(url, date, { headers });
			const { data } = results;
			this.financialSummary = !!data ? data : {};

			this.$timeout(() => {
				promiseHandler.resolve();
			});
		} catch (err) {
			this.financialSummary = {};
			this.$timeout(() => {
				promiseHandler.reject(err);
			});
		}

		return promise;
	}

	async getFinancialSummaryAttachment(id) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('FinancialSummaryAttachment');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/reporting/links');
				url = await this.InternalLinksService.getLink('FinancialSummaryAttachment');
			}

			url += `?id=${id}`;
			this.openNewTabReport(url, `Financial Summary Attachment`);
			this.$timeout(() => promiseHandler.resolve());
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(`Couldn't load API 'links'`, err));
		}

		return promise;
	}

	async getMailingList() {
		let promiseHandler;

		const promise = new Promise((resolve, reject) => {
			promiseHandler = {
				reject,
				resolve
			};
		});

		try {
			let url = await this.InternalLinksService.getLink('FinancialApiMailingList');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/financial/links');
				url = await this.InternalLinksService.getLink('FinancialApiMailingList');
			}

			const building = this._getSelectedBuildingRef();
			url = url.replace(`{schemeRef}`, building);

			const headers = this.Utility.headersWithContentType;

			const results = await this.$http.get(url, { headers });
			const { Data, WasSuccessful, Messages } = results.data;

			if (WasSuccessful) {
				this.$timeout(() => promiseHandler.resolve(this._mapCommunicationChannelDescription(Data)));
			} else {
				throw new Error(`Error while loading 'Distribution Mail List with: ${JSON.stringify(Messages)}`);
			}
		} catch (error) {
			this.$timeout(() => promiseHandler.reject(error));
		}

		return promise;
	}

	async saveMailingList(mailingList) {
		let promiseHandler;
		const promise = new Promise((resolve, reject) => {
			promiseHandler = {
				reject,
				resolve
			};
		});

		try {
			let url = await this.InternalLinksService.getLink('FinancialApiSaveMailingList');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/financial/links');
				url = await this.InternalLinksService.getLink('FinancialApiSaveMailingList');
			}

			const building = this._getSelectedBuildingRef();
			url = url.replace(`{schemeRef}`, building);

			const headers = this.Utility.headersWithContentType;
			const mailingListDetails = mailingList;

			const results = await this.$http.post(url, mailingListDetails, { headers });

			const { Data, WasSuccessful } = results.data;

			if (WasSuccessful) {
				this.$timeout(() => promiseHandler.resolve(results));
			} else {
				throw new Error(`Error while saving mailing list: ${JSON.stringify(mailingList)}`);
			}
		} catch (error) {
			this.$timeout(() => promiseHandler.reject(error));
		}

		return promise;
	}

	async printPreviewSummaryWriteUp(date) {
		let promise, promiseHandler;

		promise = new Promise((resolve, reject) => {
			promiseHandler = {
				reject,
				resolve
			};
		});

		try {
			if (!date) {
				throw new Error(`Date not provided for the Financial Summary Write-Up Preview`);
			}

			const building = this._getSelectedBuildingRef();
			const momentDateString = date.format('MMMM-YYYY');
			const title = `${building} ${momentDateString} - Financial Summary Write-Up Preview`;
			const htmlContent = this.financialSummary.Summary;

			this._renderPdf(htmlContent, title);
			this.$timeout(() => promiseHandler.resolve());
		} catch (error) {
			this.$timeout(() => promiseHandler.reject(error));
		}

		return promise;
	}

	async downloadAllAttachments(dateOfFinancials, onlyLevyRoll) {
		let promiseHandler;
		const promise = new Promise((resolve, reject) => {
			promiseHandler = {
				reject,
				resolve
			};
		});

		const schemeRef = this._getSelectedBuildingRef();
		let url = await this.InternalLinksService.getLink('FinancialsDownloadAllAttachments');
		if (!url) {
			await this.InternalLinksService.getLinks('/api/financial/links');
			url = await this.InternalLinksService.getLink('FinancialsDownloadAllAttachments');
		}

		url = url.replace('{schemeRef}', schemeRef).trim();
		url = url.replace('{dateOfFinancials}', moment(dateOfFinancials).format('YYYY-MM-DD'));

		if (url.includes('{onlyLevyRoll}')) {
			url = url.replace('{onlyLevyRoll}', onlyLevyRoll);
		} else {
			url += `&onlyLevyRoll=${onlyLevyRoll}`;
		}
		const headers = this.Utility.headersWithContentType;

		const results = await this.$http.get(url, { headers });

		const { Data, WasSuccessful } = results.data;

		if (WasSuccessful) {
			this.$timeout(() => promiseHandler.resolve(results));
		} else {
			throw new Error(`Error while getting attachments: ${JSON.stringify(dateOfFinancials)}`);
		}

		return promise;
    }

    async getbuildingWorkflowAndFeatures(schemeRef) {
        let promiseHandler = {};
        const promise = new Promise((resolve, reject) => {
            promiseHandler = {
                reject,
                resolve
            };
        });

        try {
            let url = await this.InternalLinksService.getLink('BuildingWorkflowAndFeatures');
            if (!url) {
                await this.InternalLinksService.getLinks('/api/financial/links');
                url = await this.InternalLinksService.getLink('BuildingWorkflowAndFeatures');
            }
            url = url.replace('{schemeRef}', schemeRef).trim();

            const headers = this.Utility.headersWithContentType;

            const results = await this.$http.get(url, { headers });
			const {data }= results;
            this.buildingWorkflowAndFeatures = !!data ? data : {};

            this.$timeout(() => {
                promiseHandler.resolve();
            });
        } catch (err) {
            this.buildingWorkflowAndFeatures = {};
            this.$timeout(() => {
                promiseHandler.reject(err);
            });
        }
        return promise;
	}
	
	_renderPdf(content, title) {
		const pdfWorker = html2pdf();
		const options = {
			margin: [ 20, 20, 22, 20 ],
			filename: title,
			jsPDF: { units: 'px', format: 'a4', orientation: 'landscape' }
		};

		pdfWorker.set(options).from(content).toPdf().get('pdf').then((pdf) => {
			window.open(pdf.output('bloburl'), '_blank');
		});
	}

	openNewTabReport(url, title) {
		var win = window.open(url, '_blank');
		win.focus();
		
		// const newTab = window.open(url, '_blank');
		// newTab.document.write(`
        //         <title>${title}</title>
        //         <style>
        //             html:first-child>body {
        //                 margin: 0;
        //                 overflow: hidden;
        //             }
        //             iframe {
        //                 width: 100%;
        //                 height: 100%;
        //             }
        //         </style>
        //         <iframe src="${url}">
        //         </iframe>
        //     `);
	}

	openPrintPreviewSummaryWriteUp(pdfContent, title) {
		let pdfWindow = window.open('', '_blank');
		pdfWindow.document.write(
			`<iframe width='100%' height='100%' src='data:application/pdf;base64,
				${encodeURI(pdfContent)}'>
			</iframe>`
		);
	}

	_getSelectedBuildingRef() {
		let selectedBuildingRef = this.LocalStorageService.retrieve('selectedBuildingRef');

		if (selectedBuildingRef && typeof selectedBuildingRef === 'object')
			selectedBuildingRef = selectedBuildingRef.SchemeReference;

		return selectedBuildingRef;
	}

	_mapCommunicationChannelDescription(original) {
		const mapped = original;
		var temp = mapped.EmailListItems.map((mailItem) => {
			const { CommunicationChannel } = mailItem;
			if (CommunicationChannel === 0) {
				mailItem.CommunicationChannelDescription = 'PDFs';
			} else if (CommunicationChannel === 1) {
				mailItem.CommunicationChannelDescription = 'Online';
			}

			mailItem.isEdit = false;
			mailItem.MailingListHeaderId = mapped.Id;

			return mailItem;
		});

		if (temp) {
			mapped.EmailListItems = temp;
		}

		return mapped;
	}
}

export default /* @ngInject */ ($http, Utility, $timeout, $rootScope, InternalLinksService, LocalStorageService) =>
	new FinancialSummaryService($http, Utility, $timeout, $rootScope, InternalLinksService, LocalStorageService);
